import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, Gift, Store, TrendingUp, Ruler, Target, Sparkles, ShoppingBag, Zap, Package } from 'lucide-react';

const QUESTIONS = [
    {
        id: 'platform',
        titleLine1: 'TA',
        titleLine2: 'PLATEFORME',
        subtitle: 'Chaque marketplace a ses codes,',
        subtitleHighlight: 'on s\'adapte.',
        options: [
            { value: 'vinted', label: 'Vinted', desc: 'Mode & vintage', style: 'ZIPPER', icon: ShoppingBag, color: '#09B1BA' },
            { value: 'tiktok', label: 'TikTok Shop', desc: 'Lives & viral', style: 'NEON', icon: Zap, color: '#ff0050' },
            { value: 'shopify', label: 'Shopify', desc: 'Ma boutique', style: 'UNICOLOR', icon: Store, color: '#96bf48' },
            { value: 'amazon', label: 'Amazon', desc: 'Marketplace', style: 'ZIPPER', icon: Package, color: '#ff9900' },
            { value: 'new', label: 'Je débute', desc: 'Pas encore', style: 'NEON', icon: Sparkles, color: '#00f2ea' }
        ]
    },
    {
        id: 'volume',
        titleLine1: 'TON',
        titleLine2: 'VOLUME',
        subtitle: 'Plus tu vends,',
        subtitleHighlight: 'plus on t\'offre.',
        options: [
            { value: 'starter', label: '0-30', desc: 'Je démarre', pack: 30, icon: Sparkles, color: '#00f2ea' },
            { value: 'growing', label: '30-100', desc: 'En growth', pack: 100, icon: TrendingUp, color: '#ffee00' },
            { value: 'pro', label: '100+', desc: 'Pro', pack: 300, icon: Zap, color: '#ff0050' }
        ]
    },
    {
        id: 'size',
        titleLine1: 'TAILLE',
        titleLine2: 'COLIS',
        subtitle: 'Formats pensés pour',
        subtitleHighlight: 'l\'expédition FR.',
        options: [
            { value: 'S', label: 'S', desc: 'T-shirt', icon: Package, color: '#00f2ea' },
            { value: 'M', label: 'M', desc: 'Jean, pull', icon: Package, color: '#ffee00' },
            { value: 'L', label: 'L', desc: 'Manteau', icon: Package, color: '#ff0050' }
        ]
    },
    {
        id: 'pain',
        titleLine1: 'TON',
        titleLine2: 'DÉFI',
        subtitle: 'On résout ton problème,',
        subtitleHighlight: 'lequel ?',
        options: [
            { value: 'reviews', label: 'Avis 5★', desc: 'Fidélisation', icon: Sparkles, color: '#ffee00' },
            { value: 'damage', label: '0 litige', desc: 'Protection', icon: Target, color: '#ff0050' },
            { value: 'branding', label: 'Branding', desc: 'Identité', icon: Zap, color: '#00f2ea' },
            { value: 'cost', label: 'Coûts', desc: 'Marges', icon: TrendingUp, color: '#96bf48' }
        ]
    },
    {
        id: 'ready',
        titleLine1: 'ON',
        titleLine2: 'Y VA ?',
        subtitle: 'Ton pack est prêt,',
        subtitleHighlight: 'surprise inside.',
        options: [
            { value: 'yes', label: 'Let\'s go', desc: 'Je découvre', icon: Zap, color: '#ff0050' },
            { value: 'maybe', label: 'Juste voir', desc: 'Sans engagement', icon: Sparkles, color: '#00f2ea' }
        ]
    }
];

const PromoQuiz = ({ isOpen, onClose, onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [answers, setAnswers] = useState({});
    const [isCompleted, setIsCompleted] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
            setAnswers({});
            setIsCompleted(false);
            setSelectedOption(null);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const handleAnswer = (answer) => {
        setSelectedOption(answer);
        setTimeout(() => {
            const question = QUESTIONS[currentStep];
            const newAnswers = { ...answers, [question.id]: answer };
            setAnswers(newAnswers);
            setSelectedOption(null);
            if (currentStep < QUESTIONS.length - 1) {
                setCurrentStep(currentStep + 1);
            } else {
                setIsCompleted(true);
            }
        }, 250);
    };

    const handleComplete = () => {
        const platformAnswer = QUESTIONS[0].options.find(o => o.value === answers.platform);
        const volumeAnswer = QUESTIONS[1].options.find(o => o.value === answers.volume);
        const config = {
            style: platformAnswer?.style || 'NEON',
            pack: volumeAnswer?.pack || 30,
            size: answers.size || 'M',
            pain: answers.pain,
            hasPromo: true
        };
        onComplete(config);
        onClose();
    };

    if (!isOpen) return null;

    const question = QUESTIONS[currentStep];

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[200]"
                onClick={onClose}
            >
                {/* Full black backdrop */}
                <div className="absolute inset-0 bg-black" />

                {/* Content container - MOBILE FIRST */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="relative w-full h-full flex flex-col px-6 pt-4 pb-8 overflow-y-auto"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        {/* Progress dots */}
                        <div className="flex items-center gap-2">
                            {QUESTIONS.map((_, idx) => (
                                <div
                                    key={idx}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${idx < currentStep ? 'w-6 bg-[#00f2ea]' :
                                        idx === currentStep ? 'w-10 bg-gradient-to-r from-[#ff0050] to-[#00f2ea]' :
                                            'w-4 bg-white/20'
                                        }`}
                                />
                            ))}
                        </div>
                        <button
                            onClick={onClose}
                            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 text-white/40 hover:text-white hover:bg-white/10 transition-all"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <AnimatePresence mode="wait">
                        {!isCompleted ? (
                            <motion.div
                                key={currentStep}
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -30 }}
                                transition={{ duration: 0.25 }}
                                className="flex-1 flex flex-col"
                            >
                                {/* Title section - EXACT HomePage "OBJECTIF 5 ÉTOILES" style */}
                                <div className="mb-8">
                                    <h2 className="text-5xl md:text-7xl font-black leading-none text-white">
                                        {question.titleLine1} <br />
                                        <span
                                            className="glitch-voltage text-transparent bg-clip-text bg-gradient-to-r from-[#00f2ea] to-white"
                                            data-text={question.titleLine2}
                                        >
                                            {question.titleLine2}
                                        </span>
                                    </h2>
                                    <p className="mt-6 text-xl text-gray-400 font-medium leading-relaxed">
                                        {question.subtitle}{' '}
                                        <span className="text-white font-bold">{question.subtitleHighlight}</span>
                                    </p>
                                </div>

                                {/* Options - Card grid */}
                                <div className={`grid gap-3 ${question.options.length <= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                    {question.options.map((option, idx) => {
                                        const Icon = option.icon;
                                        const isSelected = selectedOption === option.value;
                                        return (
                                            <motion.button
                                                key={option.value}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: idx * 0.05 }}
                                                onClick={() => handleAnswer(option.value)}
                                                disabled={selectedOption !== null}
                                                className={`relative p-4 rounded-2xl text-center transition-all duration-200 group overflow-hidden ${isSelected
                                                    ? 'bg-white scale-95'
                                                    : 'bg-white/[0.06] hover:bg-white/[0.12] active:scale-95'
                                                    }`}
                                                style={{
                                                    borderWidth: 1,
                                                    borderColor: isSelected ? option.color : 'rgba(255,255,255,0.1)'
                                                }}
                                            >
                                                {/* Icon */}
                                                <div
                                                    className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center transition-all ${isSelected ? 'bg-black/10' : ''
                                                        }`}
                                                    style={{
                                                        backgroundColor: isSelected ? `${option.color}20` : `${option.color}15`,
                                                    }}
                                                >
                                                    <Icon
                                                        size={24}
                                                        style={{ color: isSelected ? '#000' : option.color }}
                                                    />
                                                </div>

                                                {/* Label */}
                                                <div className={`font-black text-lg mb-0.5 transition-colors ${isSelected ? 'text-black' : 'text-white'
                                                    }`}>
                                                    {option.label}
                                                </div>

                                                {/* Desc */}
                                                <div className={`text-[11px] font-medium transition-colors ${isSelected ? 'text-black/50' : 'text-white/40'
                                                    }`}>
                                                    {option.desc}
                                                </div>
                                            </motion.button>
                                        );
                                    })}
                                </div>
                            </motion.div>
                        ) : (
                            <motion.div
                                key="completed"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex-1 flex flex-col items-center justify-center text-center px-4"
                            >
                                {/* Enhanced Gift animation */}
                                <motion.div
                                    className="relative w-36 h-36 mb-10"
                                    animate={{ y: [0, -15, 0] }}
                                    transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
                                >
                                    {/* Multiple glow layers */}
                                    <div className="absolute inset-[-30px] bg-[#ff0050]/20 rounded-full blur-[60px]" />
                                    <div className="absolute inset-[-15px] bg-[#00f2ea]/20 rounded-full blur-[40px]" />

                                    {/* Rotating ring */}
                                    <motion.div
                                        className="absolute inset-[-8px] rounded-[32px] border-2 border-dashed border-white/20"
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                                    />

                                    {/* Main gift box */}
                                    <div className="relative w-full h-full bg-gradient-to-br from-[#ff0050] via-[#ff0050] to-[#cc0040] rounded-[28px] flex items-center justify-center shadow-[0_30px_80px_rgba(255,0,80,0.5)]">
                                        <motion.div
                                            animate={{ rotate: [-5, 5, -5], scale: [1, 1.05, 1] }}
                                            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                                        >
                                            <Gift size={56} className="text-white drop-shadow-lg" />
                                        </motion.div>
                                    </div>

                                    {/* Sparkles */}
                                    {[...Array(3)].map((_, i) => (
                                        <motion.div
                                            key={i}
                                            className="absolute w-2 h-2 bg-white rounded-full"
                                            style={{
                                                top: ['10%', '-5%', '20%'][i],
                                                right: ['-10%', '20%', '-5%'][i],
                                            }}
                                            animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
                                            transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.4 }}
                                        />
                                    ))}
                                </motion.div>

                                {/* Title with glitch effect */}
                                <h2 className="text-5xl md:text-7xl font-black leading-none text-white mb-4">
                                    C'EST <br />
                                    <span
                                        className="glitch-voltage text-transparent bg-clip-text bg-gradient-to-r from-[#00f2ea] to-white"
                                        data-text="PRÊT"
                                    >
                                        PRÊT
                                    </span>
                                </h2>

                                <p className="text-xl text-gray-400 font-medium mb-10 max-w-[300px]">
                                    Une surprise t'attend dans ton <span className="text-white font-bold">configurateur personnalisé.</span>
                                </p>

                                {/* Premium CTA Button */}
                                <motion.button
                                    onClick={handleComplete}
                                    className="w-full py-5 bg-gradient-to-r from-[#ff0050] to-[#ff0050] text-white font-black text-lg uppercase tracking-wider rounded-2xl relative overflow-hidden shadow-[0_20px_50px_rgba(255,0,80,0.3)]"
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {/* Shimmer effect */}
                                    <motion.div
                                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                                        animate={{ x: ['-100%', '100%'] }}
                                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                                    />
                                    <span className="relative flex items-center justify-center gap-3">
                                        Découvrir
                                        <ChevronRight size={22} />
                                    </span>
                                </motion.button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default PromoQuiz;
